<template>
    <v-container grid-list-xl>
        <v-layout my-6>
            <v-btn @click="$router.go(-1)" class="my-auto">
                <v-icon>navigate_before</v-icon>
                {{ $t('back') }}
            </v-btn>

            <v-flex ml-12 xs10>
                <div class="display-3 font-weight-thin">{{ $i18n.t('addQuestions') }}</div>
            </v-flex>

            <v-spacer/>

            <v-flex lg3 md7 mt-7 mr-8>
                <v-text-field
                    dense
                    outlined
                    v-model="search"
                    @input="searchUpdate"
                    @keydown.enter="showById"
                    :rules="searchRules"
                    :placeholder="$i18n.t('searchQuestion')"
                    :hint="$i18n.t('searchQuestionTooltip')"
                    class="body-1 font-weight-medium"
                    ref="searchField"/>
            </v-flex>
        </v-layout>


        <v-expansion-panels class="my-6">
            <v-expansion-panel>
                <v-expansion-panel-header>
                    <div class="headline font-weight-light black--text">{{ $i18n.t('filters') }}</div>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                    <v-container grid-list-xl>
                        <v-layout row>
                            <v-flex xs2 class="subtitle-1 font-weight-light black--text mt-4">
                                {{ $i18n.t('lastUseBefore') }}
                            </v-flex>
                            <v-flex xs4>
                                <v-menu v-model="datePicker" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field v-model="filters.lastUsed" prepend-icon="today" v-on="on" :disabled="filters.neverUsed" readonly outlined/>
                                    </template>
                                    <v-date-picker v-model="filters.lastUsed" :locale="$i18n.locale" @input="datePicker = false" :max="new Date().toISOString()" scrollable no-title/>
                                </v-menu>
                            </v-flex>
                            <v-flex xs2 class="subtitle-1 font-weight-regular">
                                <v-checkbox v-model="filters.neverUsed" :label="$i18n.t('neverUsed')"/>
                            </v-flex>
                        </v-layout>

                        <v-layout row my-n10>
							<v-flex xs2 class="subtitle-1 font-weight-light black--text mt-4">
								<v-tooltip open-on-hover bottom>
									<template v-slot:activator="{ on }">
										<div v-on="on">
											{{ $i18n.t('difficulty') }}
										</div>
									</template>
									<span>{{ $i18n.t('difficultyTooltip') }}</span>
								</v-tooltip>
							</v-flex>

                            <v-flex xs2>
                                <v-text-field v-model="filters.minDifficulty" label="Min" readonly outlined
                                    prepend-icon="remove"
                                    @click:prepend="filters.minDifficulty = down(filters.minDifficulty, 0.0, 10.0)"
                                    append-outer-icon="add"
                                    @click:append-outer="filters.minDifficulty = up(filters.minDifficulty, filters.maxDifficulty, 10.0)"/>
                            </v-flex>
                            <v-flex xs2 mx-8>
                                <v-text-field v-model="filters.maxDifficulty" label="Max" readonly outlined
                                    prepend-icon="remove"
                                    @click:prepend="filters.maxDifficulty = down(filters.maxDifficulty, filters.minDifficulty, 10.0)"
                                    append-outer-icon="add"
                                    @click:append-outer="filters.maxDifficulty = up(filters.maxDifficulty, 100.0, 10.0)"/>
                            </v-flex>
                        </v-layout>

                        <v-layout row my-n10>
							<v-flex xs2 class="subtitle-1 font-weight-light black--text mt-4">
								<v-tooltip open-on-hover bottom>
									<template v-slot:activator="{ on }">
										<div v-on="on">
											{{ $i18n.t('discrimination') }}
										</div>
									</template>
									<span>{{ $i18n.t('discriminationTooltip') }}</span>
								</v-tooltip>
							</v-flex>

                            <v-flex xs2>
                                <v-text-field v-model="filters.minDiscrimination" label="Min" readonly outlined
                                    prepend-icon="remove"
                                    @click:prepend="filters.minDiscrimination = down(filters.minDiscrimination, 0.0, 0.1).toFixed(1)"
                                    append-outer-icon="add" 
                                    @click:append-outer="filters.minDiscrimination = up(filters.minDiscrimination, filters.maxDiscrimination, 0.1).toFixed(1)"/>
                            </v-flex>
                            <v-flex xs2 mx-8>
                                <v-text-field v-model="filters.maxDiscrimination" label="Max" readonly outlined
                                    prepend-icon="remove"
                                    @click:prepend="filters.maxDiscrimination = down(filters.maxDiscrimination, filters.minDiscrimination, 0.1).toFixed(1)"
                                    append-outer-icon="add"
                                    @click:append-outer="filters.maxDiscrimination = up(filters.maxDiscrimination, 1.0, 0.1).toFixed(1)"/>
                            </v-flex>
                        </v-layout>

                        <v-layout multiple row my-n10>
                            <v-radio-group row v-model="basicFilter">
                                <v-radio class="mt-5 mb-7" :label="$i18n.t('showBasic')"/>
                                <v-radio class="mt-5 mb-7" :label="$i18n.t('showAdvanced')"/>
                                <v-radio class="mt-5 mb-7" :label="$i18n.t('showAll')"/>
                            </v-radio-group>
                        </v-layout>

                        <v-layout row my-n10>
                            <v-radio-group row v-model="imageFilter">
                                <v-radio class="mt-5 mb-7" :label="$i18n.t('showWithImage')"/>
                                <v-radio class="mt-5 mb-7" :label="$i18n.t('showWithoutImage')"/>
                                <v-radio class="mt-5 mb-7" :label="$i18n.t('showAll')"/>
                            </v-radio-group>
                        </v-layout>

                        <v-layout row>
                            <v-spacer/>
                            <v-btn class="my-auto mr-5" @click="resetFilters">
                                {{ $t('restartFilters') }}
                                <v-icon right>replay</v-icon>
                            </v-btn>
                            <v-btn color="teal" class="my-auto" :loading="isLoading" :disabled="isLoading" @click="applyFilters">
                                {{ $t('applyFilters') }}
                                <v-icon right>done</v-icon>
                            </v-btn>
                        </v-layout>
                    </v-container>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <v-expansion-panels v-model="activeTopic">
            <v-expansion-panel v-for="topic in getTopics" :key="topic.id" @click="viewTopic(topic)">
                <v-expansion-panel-header>
                    <v-row>
                        <div class="mx-3 title">
                            {{ $i18n.t('TOPIC') + ' ' + topic.position }}
                        </div>
                        <div class="my-auto subtitle-1">
                            {{ topic.name }}
                        </div>
                        <v-spacer/>

                        <div class="my-auto mr-4">
                            {{ topic.numberOfQuestions +
                               ' ' +
                               (topic.numberOfQuestions == 1 ? $i18n.t('question') : $i18n.t('questions')) +
                               ' / ' +
                               getNumberOfSelectedQuestions(topic) +
                               ' ' +
                               (getNumberOfSelectedQuestions(topic) == 1 ? $i18n.t('selectedSingular') : $i18n.t('selected'))
                            }}
                        </div>
                    </v-row>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                    <v-simple-table class="ml-7">
                        <template v-slot:default>
                            <tbody>
                                <tr v-for="question in getQuestions" :key="question.id">
                                    <td class="subtitle-1 font-weight-light">{{ question.id }}</td>
                                    <td width="90%" @click="viewQuestion(question)">{{ question.title || question.text }}</td>

                                    <td>
                                        <v-tooltip open-on-hover open-delay=200 max-width=220 bottom v-if="getTestQuestions.includes(question.id)">
                                            <template v-slot:activator="{ on }">
                                                    <v-btn v-on="on" @click="removeQuestion(question)" icon>
                                                        <v-icon color="grey">remove</v-icon>
                                                    </v-btn>
                                            </template>
                                            <span>{{ $i18n.t('deleteQuestionTooltip') }}</span>
                                        </v-tooltip>

                                        <v-tooltip open-on-hover open-delay=200 max-width=220 bottom v-else>
                                            <template v-slot:activator="{ on }">
                                                    <v-btn v-on="on" @click="addQuestion(question)" icon>
                                                        <v-icon color="teal">add</v-icon>
                                                    </v-btn>
                                            </template>
                                            <span>{{ $i18n.t('addQuestion') }}</span>
                                        </v-tooltip>
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <v-dialog v-model="viewQuestionDialog" max-width="1300px">
            <EditQuestion v-if="isEditingQuestion"/>
            <QuestionInfo v-else/>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import QuestionInfo from '@/components/Question/QuestionInfo'
import EditQuestion from '@/components/Question/EditQuestion'

function initFilters() {
    return {
        filters: {
            maxDifficulty: "100",
            minDifficulty: "0",
            maxDiscrimination: "1.0",
            minDiscrimination: "0.0",
            withImage: true,
            withoutImage: true,
            basic: true,
            advanced: true,
            lastUsed: new Date().toISOString().substr(0, 10),
            neverUsed: false,
            hidden: true,
        },

        datePicker: false,
        activeTopic: undefined,

        search: null,
        questionExists: true,
        questionInSubject: true,

        searchRules: [
            v => this.questionExists || this.$i18n.t('noQuestionFound'),
            v => this.questionInSubject || this.$i18n.t('questionNotInSubject')
        ],
    }
}

export default {
    data: initFilters,

    methods: {
        viewTopic(topic){
            this.$store.dispatch('getQuestionsByTopic', { topicId: topic.id, filter: this.filters })
        },
        viewQuestion(question){
            this.$store.commit('setEditingQuestion', false)
            this.$store.commit('setQuestion', question)
            this.$store.dispatch('getFormulationsByQuestion', question.id)
            if (question.image)
                this.$store.dispatch('getImage', question.id)

            this.$store.commit('setViewQuestionDialog', true)
        },
        getNumberOfSelectedQuestions(topic){
            return this.getFormulationsByTest.filter(formulation => formulation.question.topicId == topic.id).length
        },
        addQuestion(question){
            this.$store.dispatch('addFormulation', { questionId: question.id, testId: this.$route.params.id })
        },
        removeQuestion(question){
            this.$store.dispatch('deleteFormulation', question.id)
        },
        applyFilters(){
            this.$store.dispatch('getTopics', this.filters)
            this.activeTopic = undefined
        },
        resetFilters(){
            Object.assign(this.$data, initFilters())
        },
        searchUpdate(){
            this.questionExists = true
            this.questionInSubject = true
            this.$refs.searchField.validate()
        },
        showById(){
            this.$store.dispatch('getQuestion', this.search)
            .then((question) => {
                if (question.subjectId == this.getCurrentSubjectId) {
                    this.$store.commit('setQuestions', [question])
                    this.viewQuestion(question)
                } else {
                    this.questionInSubject = false
                    this.$refs.searchField.validate()
                }
            })
            .catch(() => {
                this.questionExists = false
                this.$refs.searchField.validate()
            })
        },

        up(value, max, step){
            value = parseFloat(value)
            if (value+step < max)
                return value+step
            else
                return parseFloat(max)
        },
        down(value, min, step){
            value = parseFloat(value)
            if (value-step > min)
                return value-step
            else
                return parseFloat(min)
        }
    },

    created(){
        this.$store.dispatch('getTopics', this.filters)
        this.$store.dispatch('getFormulationsByTest', this.$route.params.id)
    },

    components: {
        QuestionInfo,
        EditQuestion,
    },

    computed: {
        ...mapGetters([
            'isLoading',
            'isAdmin',
            'getTopics',
            'getViewQuestionDialog',
            'isEditingQuestion',
            'getTestQuestions',
            'getFormulationsByTest',
            'getQuestions',
            'getCurrentSubjectId',
        ]),
        viewQuestionDialog: {
            get() { return this.getViewQuestionDialog },
            set(value) { this.$store.commit('setViewQuestionDialog', value) },
        },
        basicFilter: {
            get() {
                if (this.filters.basic && this.filters.advanced) return 2
                else if (this.filters.basic) return 0
                else if (this.filters.advanced) return 1
            },
            set(value) {
                if (value == 0) {
                    this.filters.basic = true
                    this.filters.advanced = false
                } else if (value == 1) {
                    this.filters.basic = false
                    this.filters.advanced = true
                } else if (value == 2) {
                    this.filters.basic = true
                    this.filters.advanced = true
                }
            }
        },
        imageFilter: {
            get() {
                if (this.filters.withImage && this.filters.withoutImage) return 2
                else if (this.filters.withImage) return 0
                else if (this.filters.withoutImage) return 1
            },
            set(value) {
                if (value == 0) {
                    this.filters.withImage = true
                    this.filters.withoutImage = false
                } else if (value == 1) {
                    this.filters.withImage = false
                    this.filters.withoutImage = true
                } else if (value == 2) {
                    this.filters.withImage = true
                    this.filters.withoutImage = true
                }
            }
        }
    },
}
</script>
